import React, { useCallback, useMemo, useRef, useState } from "react";
import { addHours, format } from "date-fns";
import { generatePath, Link, useSearchParams } from "react-router-dom";
import { Routes } from "router";
import { ArrowRight, Edit } from "theme/icons";

import { RawOptionsFromRefiners } from "@fullcalendar/core/internal";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import { Box, CircularProgress, Grid2, useTheme } from "@mui/material";

import { useBookings } from "../../../api/hooks/useBookings/useBookings.ts";
import useHalls from "../../../api/hooks/useHalls/useHalls.ts";
import { useUpdateBooking } from "../../../api/hooks/useUpdateBooking/useUpdateBooking.ts";
import Typography from "../../../theme/components/Typography/Typography.tsx";
import { convertStingToDate } from "../../../utils/dateHelpers.ts";

import { StyledPopover } from "./WeekCalendar.tsx";

class ResourceRefiners {
}

const CalendarComponent: React.FC = () => {
  const calendarRef = useRef<FullCalendar | null>(null);
  const { mutateAsync } = useUpdateBooking('');
  const { palette } = useTheme();


  const [searchParams, setSearchParams] = useSearchParams();

  const { halls, isFetching } = useHalls();

  const resources = useMemo<RawOptionsFromRefiners<Required<ResourceRefiners>>>(() => halls?.map((h) => ({
    id: String( h.id),
    title: h.name,
  })) || [], [halls]);

  const { data, isPending: isBookingsFetching, refetch } = useBookings({
    startDate: format(convertStingToDate(searchParams.get("d")) ?? new Date(), "yyyy-MM-dd"),
    endDate: format(convertStingToDate(searchParams.get("d")) ?? new Date(), "yyyy-MM-dd"),
    hallId: null,
    page: 1,
    pageSize: 120,
    sendDeleted: false
  });

  const getEventBackgroundColor = useCallback((status: string) => {
    switch (status) {
      case "PaymentPending":
        return palette.warning.main;
      case "PaymentCompleted":
        return palette.success.main;
      case "PaymentMade":
        return '#FFA500';
      case "PaymentCanceled":
        return palette.error.main;
      default:
        return palette.error.main;
    }
  }, [palette.warning.main, palette.success.main, palette.error.main])

  const eventDropped = useCallback(async (eventDropInfo: any) => {
    const result = confirm('Підвердити зміну дати?');

    if (!result){
      eventDropInfo.revert();
    }else{
      try {
        await mutateAsync({
          id: eventDropInfo.event.id,
          hallId: eventDropInfo.newResource?.id || null,
          startDate: format(new Date(eventDropInfo.event.startStr), "yyyy-MM-dd'T'HH:mm:ss.SSS"),
          endDate: format(new Date(eventDropInfo.event.endStr), "yyyy-MM-dd'T'HH:mm:ss.SSS"),
        })
        await refetch();
      }catch (e){
        console.error(e);
        eventDropInfo.revert();
      }
    }
  }, [mutateAsync, refetch]);

  const tableEvents = useMemo(() => data?.bookings?.map(b => ({
    id: b.bookingId,
    title: `${b.clientName} ${b.clientPhoneNumber}`,
    durationEditable: false,
    editable: true,
    resourceId: b.hallId,
    start: format(b.startDate, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
    end: format(b.endDate, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
    backgroundColor: getEventBackgroundColor(b.paymentStatus),
    textColor: "black",
    extendedProps: {
      name: b.clientName,
      phone: b.clientPhoneNumber,
      price: b.priceWithDiscounts,
      range: b.durationSeconds / 3600,
      id: b.bookingId,
      comment: b.clientComment
    }
  })) || [], [data?.bookings, getEventBackgroundColor])

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [info, setInfo] = useState<any>(null);
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  if (isFetching || isBookingsFetching) {
    return <Grid2 container justifyContent="center">
      <CircularProgress color="secondary" />
    </Grid2>;
  }

  return (
    <Box>
      <FullCalendar
        initialDate={convertStingToDate(searchParams.get("d")) ?? new Date()}
        datesSet={date => setSearchParams((prev) => {
          prev.set("d", date.startStr ? format(date.startStr, "yyyy-MM-dd") : "");

          return prev;
        })}
        events={tableEvents as any}
        ref={calendarRef}
        schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
        plugins={[dayGridPlugin, resourceTimeGridPlugin, interactionPlugin]}
        initialView="resourceTimeGridDay"
        selectable
        editable
        eventConstraint={{
          start: addHours(new Date(), -2),
        }}
        slotMinTime="07:00:00"
        slotMaxTime="23:30:00"
        eventOverlap={false}
        eventDurationEditable={false}
        displayEventEnd
        displayEventTime
        allDaySlot
        resources={resources}
        eventDrop={eventDropped}
        eventClick={info => {
          setInfo(info.event.extendedProps);
          setAnchorEl(info.el as any);
        }}
      />

      <StyledPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        disableRestoreFocus
      >
        {info && <Grid2 container flexDirection="column" gap={1}>
          <Typography variant="body1" weight='semiBold'>{`Ім'я: ${info.name}`}</Typography>
          <Typography variant="body1" weight='semiBold'>{`Телефон: ${info.phone}`}</Typography>
          <Typography variant="body1" weight='semiBold'>{`Ціна: ${info.price}грн`}</Typography>
          <Typography variant="body1" weight='semiBold'>{`Оренда: ${info.range}год`}</Typography>
          {
            info.comment && <Typography
              variant="body1"
              weight="semiBold"
            >{`Коментар: ${info.comment}`}</Typography>
          }

          <Link to={generatePath(Routes.BookingById, {
            id: info.id
          })}>Редагувати{" "}<Edit/></Link>

          <Link to={generatePath(Routes.BookingDetails, {
            id: info.id
          })}>Переглянути{" "}<ArrowRight/></Link>
        </Grid2>}
      </StyledPopover>
    </Box>
  );
};

export default CalendarComponent;
